import { forwardRef } from 'react';
import type { CheckboxInputProps } from './CheckboxInput';
import { CheckboxInput } from './CheckboxInput';
import type { BaseOptionInputProps } from '../BaseOptionInput';
import { BaseOptionInput } from '../BaseOptionInput';
import styled from '@emotion/styled';

export interface CheckboxProps
  extends CheckboxInputProps,
    Omit<BaseOptionInputProps, 'children'> {}

const StyledOptionInput = styled(BaseOptionInput)`
  width: fit-content;
`;

/**
 * A checkbox lets users select and unselect options from a list.
 *
 * {@link https://zeroheight.com/76c439891/v/latest/p/47073c-checkbox/b/21be73 Usage guidelines}
 *
 * {@link https://storybook.hotjar.com/?path=/docs/components-checkbox--checkbox Code documentation}
 *
 * @example
 * <Checkbox labelContent='My checkbox label' />
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      className,
      labelContent,
      helperText,
      labelHtmlProps = {},
      isDisabled = false,
      isChecked = false,
      isIndeterminate = false,
      isInvalid = false,
      isRequired = false,
      ...props
    }: CheckboxProps,
    ref,
  ) => {
    return (
      <StyledOptionInput
        className={className}
        isDisabled={isDisabled}
        labelContent={labelContent}
        helperText={helperText}
        labelHtmlProps={labelHtmlProps}
      >
        <CheckboxInput
          ref={ref}
          isDisabled={isDisabled}
          isChecked={isChecked}
          isIndeterminate={isIndeterminate}
          isInvalid={isInvalid}
          isRequired={isRequired}
          {...props}
        />
      </StyledOptionInput>
    );
  },
);

Checkbox.displayName = 'Checkbox';
